import { PdfTile } from "@/components/posting/parts";
import {
    EXAMPLE_RECORD,
    listCounts,
    trackOf,
    type ExampleTrackStatus,
} from "@/lib/exampleTracking";
import { CARD } from "./JobLine";

export const CHIP = "shrink-0 rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold";

const DOT: Record<"done" | "ended" | "open", string> = {
    done: "bg-green",
    ended: "bg-fg-faint",
    open: "bg-line-strong",
};

/** A row's three steps, found, tailored, and how it ended, as dots joined by a line. */
export function Track({ status }: { status: ExampleTrackStatus }) {
    const steps = trackOf(status);
    return (
        <span className="flex shrink-0 items-center gap-[3px]" aria-hidden="true">
            {steps.map((step, index) => (
                <span key={index} className="flex items-center gap-[3px]">
                    {index > 0 && (
                        <span
                            className={`h-0.5 w-3.5 rounded-full ${step === "done" ? "bg-green" : "bg-line"}`}
                        />
                    )}
                    <span className={`size-2 rounded-full ${DOT[step]}`} />
                </span>
            ))}
        </span>
    );
}

/** The record of the send to orbital: each step with when it happened, and the resume and links it kept. */
export function Record() {
    return (
        <div className="flex flex-col gap-2.5 px-[18px] pb-4">
            <ol
                aria-label={`What happened to ${EXAMPLE_RECORD.job.title} at ${EXAMPLE_RECORD.job.company}`}
                className="m-0 flex list-none flex-col gap-2 border-l-2 border-line-strong p-0 pl-3.5"
            >
                {EXAMPLE_RECORD.events.map((event) => (
                    <li key={event.what} className="relative flex flex-wrap gap-x-3 text-[13px]">
                        <span
                            className="absolute top-[5px] -left-[19px] size-2 rounded-full bg-green"
                            aria-hidden="true"
                        />
                        <span className="w-[84px] shrink-0 text-fg-faint">{event.when}</span>
                        <span>{event.what}</span>
                    </li>
                ))}
            </ol>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-4">
                <span className="inline-flex items-center gap-2">
                    <PdfTile size="sm" />
                    <span className="flex flex-col">
                        <span className="text-[12.5px] font-semibold">Tailored resume</span>
                        <span className="font-mono text-[11px] text-fg-faint">
                            {EXAMPLE_RECORD.resumeMeta}
                        </span>
                    </span>
                </span>
                {["Posting", "Apply"].map((label) => (
                    <span
                        key={label}
                        className="inline-flex h-[26px] items-center rounded-[7px] border border-line px-2 text-[11.5px] font-semibold text-fg-muted"
                    >
                        {label}
                    </span>
                ))}
            </div>
        </div>
    );
}

/** Jordan's saved lists and how many each holds: where the jobs being tailored came from. */
export function ListsCard() {
    return (
        <aside aria-label="Saved lists" className={`${CARD} flex flex-col gap-2 px-[18px] py-4`}>
            <h2 className="m-0 text-[13px] font-semibold">Saved lists</h2>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {listCounts().map((list) => (
                    <li
                        key={list.name}
                        className="flex justify-between gap-3 text-[13px] text-fg-muted"
                    >
                        {list.name}
                        <span className="font-mono text-fg">{list.count}</span>
                    </li>
                ))}
            </ul>
            <p className="m-0 border-t border-line-soft pt-2 text-[12px] text-fg-faint">
                Save as you browse. Tailor from a list when you are ready.
            </p>
        </aside>
    );
}
